/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/indent */
import { Size } from './../primitives/size';
import { Point } from './../primitives/point';
import { processPathData, pathSegmentCollection, getRectanglePath } from './../utility/path-util';
import { overFlow } from './../utility/base-util';
import { createHtmlElement, setChildPosition } from './../utility/dom-util';
import { PathElement } from '../core/elements/path-element';
import { TextElement } from '../core/elements/text-element';
import { ImageElement } from '../core/elements/image-element';
import { ElementAction, FlipDirection } from '../enum/enum';
import { DiagramElement } from '../core/elements/diagram-element';
import { Diagram } from '../diagram';
/**
 * Canvas Renderer
 */
/** @private */
export class CanvasRenderer {
    /**
     * Provide the context value for the canvas \
     *
     *  @returns {CanvasRenderingContext2D} Provide the context value for the canvas .\
     *  @param { HTMLCanvasElement} canvas - Return the dashed array values .
     *  @private
     */
    static getContext(canvas) {
        return canvas.getContext('2d');
    }
    static setCanvasSize(canvas, width, height) {
        if (canvas) {
            canvas.setAttribute('width', width.toString());
            canvas.setAttribute('height', height.toString());
        }
    }
    /**
     * Draw the gradient for the diagram shapes .\
     *
     *  @returns {SVGElement} Draw the gradient for the diagram shapes.
     *  @param {StyleAttributes} options - Provide the options  for the gradient  element .
     *  @param {SVGElement} ctx - Provide canvas values .
     *  @param {string} x - Provide the x value for the gradient .
     *  @param {string} y - Provide the x value for the gradient .
     *  @private
     */
    renderGradient(options, ctx, x, y) {
        let max;
        let min;
        let grd;
        if (options.gradient.type !== 'None') {
            for (let i = 0; i < options.gradient.stops.length; i++) {
                max = max !== undefined ? options.gradient.stops[parseInt(i.toString(), 10)].offset
                    : Math.max(max, options.gradient.stops[parseInt(i.toString(), 10)].offset);
                min = min !== undefined ? options.gradient.stops[parseInt(i.toString(), 10)].offset
                    : Math.min(min, options.gradient.stops[parseInt(i.toString(), 10)].offset);
            }
            if (options.gradient.type === 'Linear') {
                const linear = options.gradient;
                //952013 - Gradient is not rendered correctly upon export
                let x1 = linear.x1;
                let x2 = linear.x2;
                let y1 = linear.y1;
                let y2 = linear.y2;
                if (linear.controlParent instanceof Diagram && linear.controlParent.mode === 'SVG') {
                    x1 = options.width ? x1 * options.width / 100 : x1;
                    x2 = options.width ? x2 * options.width / 100 : x2;
                    y1 = options.height ? y1 * options.height / 100 : y1;
                    y2 = options.height ? y2 * options.height / 100 : y2;
                    if (x1 !== x2 && y1 !== y2) {
                        const xdistance = x2 - x1;
                        const ydistance = y2 - y1;
                        const angleRad = Math.atan2(ydistance, xdistance);
                        let angleDeg = (angleRad * (180 / Math.PI)) % 360;
                        angleDeg = angleDeg < 0 ? angleDeg + 360 : angleDeg;
                        const inverseAngleDeg = 90 - angleDeg;
                        const inverseAngleRad = inverseAngleDeg * (Math.PI / 180);
                        const effectiveDistance = (xdistance * Math.sin(angleRad)) + (ydistance * Math.cos(angleRad));
                        x2 = x1 + effectiveDistance * Math.cos(inverseAngleRad);
                        y2 = y1 + effectiveDistance * Math.sin(inverseAngleRad);
                    }
                }
                grd = ctx.createLinearGradient(x + x1, y + y1, x + x2, y + y2);
            }
            else {
                const radial = options.gradient;
                grd = ctx.createRadialGradient(x + radial.fx, y + radial.fy, 0, x + radial.cx, y + radial.cy, radial.r);
            }
            for (let i = 0; i < options.gradient.stops.length; i++) {
                const stop = options.gradient.stops[parseInt(i.toString(), 10)];
                const offset = min < 0 ? (max + stop.offset) / (2 * max) : stop.offset / max;
                grd.addColorStop(offset, stop.color);
            }
            ctx.fillStyle = grd;
        }
        return ctx;
    }
    /**
     * Draw the shawdow  for the rectangle shape in diagram \
     *
     *  @returns {void}  Draw the shawdow  for the rectangle shape in diagram .\
     *
     *  @param { SVGElement} options - Provide the base attributes .
     *  @param { RectAttributes} canvas - Provide the canvas values .
     *  @param { string} collection - Provide the collection value.
     *  @private
     */
    renderShadow(options, canvas, collection = null) {
        const ctx = CanvasRenderer.getContext(canvas);
        ctx.save();
        ctx.beginPath();
        ctx.strokeStyle = ctx.fillStyle = options.shadow.color;
        ctx.globalAlpha = options.shadow.opacity;
        const ptModel = { x: 0, y: 0 };
        const point = Point.transform(ptModel, options.shadow.angle, options.shadow.distance);
        const transX = options.x + point.x;
        const transY = options.y + point.y;
        const pivotX = transX + options.width * options.pivotX;
        const pivotY = transY + options.height * options.pivotY;
        this.rotateContext(canvas, options.angle, pivotX, pivotY);
        if (collection) {
            ctx.translate(transX, transY);
            this.renderPath(canvas, options, collection);
            ctx.translate(-transX, -transY);
        }
        else {
            ctx.rect(transX, transY, options.width, options.height);
            ctx.fillRect(transX, transY, options.width, options.height);
        }
        ctx.fill();
        ctx.stroke();
        ctx.closePath();
        ctx.restore();
    }
    /**
     * Create canvas element for the diagram \
     *
     *  @returns {HTMLCanvasElement}    Create canvas element for the diagram .\
     *
     *  @param { SVGElement} id - Provide the id for the canvas.
     *  @param { Object} width - Provide the width for the canvas.
     *  @param { Object} height - Provide the height for the canvas.
     *  @private
     */
    static createCanvas(id, width, height) {
        const canvasObj = createHtmlElement('canvas', { 'id': id });
        this.setCanvasSize(canvasObj, width, height);
        return canvasObj;
    }
    setStyle(canvas, style) {
        const ctx = CanvasRenderer.getContext(canvas);
        if (style.fill === 'none') {
            style.fill = 'transparent';
        }
        if (style.stroke === 'none') {
            style.stroke = 'transparent';
        }
        ctx.strokeStyle = style.stroke;
        ctx.lineWidth = style.strokeWidth;
        if (style.strokeWidth === 0) {
            ctx.strokeStyle = 'transparent';
        }
        ctx.globalAlpha = style.opacity;
        let dashArray = [];
        if (style.dashArray) {
            dashArray = this.parseDashArray(style.dashArray);
        }
        ctx.setLineDash(dashArray);
        if (style.gradient && style.gradient.type !== 'None') {
            if (style.shapeType === 'Rectangle') {
                this.renderGradient(style, ctx, style.x, style.y);
            }
            else {
                this.renderGradient(style, ctx, 0, 0);
            }
        }
        else {
            ctx.fillStyle = style.fill;
        }
    }
    rotateContext(canvas, angle, x, y) {
        const ctx = CanvasRenderer.getContext(canvas);
        ctx.translate(x, y);
        ctx.rotate(angle * Math.PI / 180);
        ctx.translate(-x, -y);
    }
    setFontStyle(canvas, text) {
        const ctx = CanvasRenderer.getContext(canvas);
        let font = '';
        if (text.italic) {
            font += 'italic ';
        }
        if (text.bold) {
            font += 'bold ';
        }
        font += (text.fontSize) + 'px ';
        font += text.fontFamily;
        ctx.font = font;
    }
    /**
     * Return the dashed array values \
     *
     *  @returns {number[]}  Return the dashed array values .\
     *  @param { SVGElement} dashArray - Return the dashed array values .
     *  @private
     */
    parseDashArray(dashArray) {
        const dashes = [];
        const separator = dashArray.indexOf(' ') !== -1 ? ' ' : ',';
        const splittedDashes = dashArray.split(separator);
        for (const i of splittedDashes) {
            dashes.push(Number(i));
        }
        return dashes;
    }
    drawRoundedRect(canvas, options) {
        const context = CanvasRenderer.getContext(canvas);
        context.beginPath();
        const x = options.x;
        const y = options.y;
        const w = options.width;
        const h = options.height;
        const mx = x + w / 2;
        const my = y + h / 2;
        context.beginPath();
        this.setStyle(canvas, options);
        context.moveTo(x, my);
        context.quadraticCurveTo(x, y, mx, y);
        context.quadraticCurveTo(x + w, y, x + w, my);
        context.quadraticCurveTo(x + w, y + h, mx, y + h);
        context.quadraticCurveTo(x, y + h, x, my);
        //892454-Fill color not applied for BPMN activity shapes inside the symbol palette.
        context.fill();
        context.stroke();
    }
    //Rendering Part
    /**
     * Draw the Rectangle for the diagram \
     *
     *  @returns {void}  Draw the Rectangle for the diagram .\
     *
     *  @param { SVGElement} canvas - Provide the SVG .
     *  @param { RectAttributes} options - Provide the Rect attributes .
     *  @param { string} diagramId - Provide the diagram id .
     *  @param { boolean} isExport - Provide the isExport .
     *  @param { boolean} isSelector - Provide the selector possobilities .
     *  @param { SVGSVGElement} parentSvg - Provide the parent svg element .
     *  @param { Object} ariaLabel - Provide the Arial label attributes .
     *  @param { boolean} isCircularHandle - Provide the boolean attribute for the circular handle .
     *  @param { number} enableSelector - Provide the selector possobilities .
     *  @param { any } renderer - Provide the renderer value .
     *  @param { any } element - Provide the element value .
     *  @private
     */
    drawRectangle(canvas, options, diagramId, isExport, isSelector, parentSvg, ariaLabel, isCircularHandle, enableSelector, renderer, element) {
        if (options.visible === true) {
            if (options.cornerRadius) {
                if (!isExport && (options.width < 30 || options.height < 30)) {
                    this.drawRoundedRect(canvas, options);
                }
                else {
                    options.data = getRectanglePath(options.cornerRadius, options.height, options.width);
                    this.drawPath(canvas, options, diagramId, isSelector, parentSvg, ariaLabel, undefined, renderer, element);
                }
            }
            else {
                const ctx = CanvasRenderer.getContext(canvas);
                if (options.shadow) {
                    this.renderShadow(options, canvas);
                }
                ctx.save();
                ctx.beginPath();
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const cornerRadius = options.cornerRadius;
                const pivotX = options.x + options.width * options.pivotX;
                const pivotY = options.y + options.height * options.pivotY;
                //To flip the annotation background rect.
                if (options.textWrapping && options.wrapBounds) {
                    this.applyFlipAndRotate(ctx, options, canvas, pivotX, pivotY, renderer, element);
                }
                else {
                    const angle = options.isImage ? -options.angle : options.angle;
                    this.rotateContext(canvas, angle, pivotX, pivotY);
                }
                this.setStyle(canvas, options);
                ctx.rect(options.x, options.y, options.width, options.height);
                ctx.fillRect(options.x, options.y, options.width, options.height);
                ctx.fill();
                ctx.stroke();
                ctx.closePath();
                ctx.restore();
            }
        }
    }
    // public updateSelectionRegion(canvas: HTMLCanvasElement, options: RectAttributes): void {
    //     this.drawRectangle(canvas, options);
    // }
    // public drawLine(canvas: HTMLCanvasElement, options: LineAttributes): void {
    //     let ctx: CanvasRenderingContext2D = CanvasRenderer.getContext(canvas);
    //     ctx.save();
    //     ctx.beginPath();
    //     let pivotX: number = options.x + options.width * options.pivotX;
    //     let pivotY: number = options.y + options.height * options.pivotY;
    //     this.rotateContext(canvas, options.angle, pivotX, pivotY);
    //     this.setStyle(canvas, options as StyleAttributes);
    //     ctx.translate(options.x, options.y);
    //     ctx.moveTo(options.startPoint.x, options.startPoint.y);
    //     ctx.lineTo(options.endPoint.x, options.endPoint.y);
    //     ctx.translate(-options.x, -options.y);
    //     ctx.stroke();
    //     ctx.closePath();
    //     ctx.restore();
    // }
    // public drawCircle(canvas: HTMLCanvasElement, options: CircleAttributes): void {
    //     let ctx: CanvasRenderingContext2D = CanvasRenderer.getContext(canvas);
    //     ctx.save();
    //     ctx.beginPath();
    //     let pivotY: number = options.y + options.height * options.pivotY;
    //     let pivotX: number = options.x + options.width * options.pivotX;
    //     this.setStyle(canvas, options as StyleAttributes);
    //     this.rotateContext(canvas, options.angle, pivotX, pivotY);
    //     ctx.arc(options.centerX, options.centerY, options.radius, 0, 2 * Math.PI);
    //     ctx.fill();
    //     ctx.stroke();
    //     ctx.closePath();
    //     ctx.restore();
    // }
    /**
     * Draw the path element for the diagram\
     *
     *  @returns {void}  Draw the path element for the diagram .\
     *
     *  @param { SVGElement} canvas - Provide the SVG element .
     *  @param { PathAttributes} options - Provide the path element attributes .
     *  @param {string} diagramId - Provide the diagram id .
     *  @param {boolean} isSelector - Provide selector boolean value .
     *  @param {SVGSVGElement} parentSvg - Provide the parent SVG element .
     *  @param {Object} ariaLabel - Provide arial label value .
     *  @param {number} scale - Provide the scale value .
     *  @param {any} renderer - Provide the renderer value .
     *  @param {PathElement} element - Provide the path element value .
     *  @private
     */
    drawPath(canvas, options, diagramId, isSelector, parentSvg, ariaLabel, scale, renderer, element) {
        let collection = [];
        collection = processPathData(options.data);
        collection = pathSegmentCollection(collection);
        if (options.shadow) {
            this.renderShadow(options, canvas, collection);
        }
        const ctx = CanvasRenderer.getContext(canvas);
        ctx.save();
        ctx.beginPath();
        const pivotY = options.y + options.height * options.pivotY;
        const pivotX = options.x + options.width * options.pivotX;
        this.applyFlipAndRotate(ctx, options, canvas, pivotX, pivotY, renderer, element);
        this.setStyle(canvas, options);
        ctx.translate(options.x, options.y);
        this.renderPath(canvas, options, collection);
        ctx.fill();
        ctx.translate(-options.x, -options.y);
        ctx.stroke();
        ctx.restore();
    }
    /**
     * Draw the path element for the diagram\
     *
     *  @returns {void}  Draw the path element for the diagram .\
     *
     *  @param { SVGElement} canvas - Provide the SVG element .
     *  @param {PathAttributes} options - Provide the path element attributes .
     *  @param {Object[]} collection - Provide the parent SVG element .
     *  @private
     */
    renderPath(canvas, options, collection) {
        if (options.visible === true) {
            let arcCount = 0;
            const ctx = CanvasRenderer.getContext(canvas);
            let x0;
            let y0;
            let x1;
            let y1;
            let x2;
            let y2;
            let x;
            let y;
            let length;
            let i;
            const segs = collection;
            for (x = 0, y = 0, i = 0, length = segs.length; i < length; ++i) {
                const obj = segs[parseInt(i.toString(), 10)];
                const seg = obj;
                const char = seg.command;
                if ('x1' in seg) {
                    x1 = seg.x1;
                }
                if ('x2' in seg) {
                    x2 = seg.x2;
                }
                if ('y1' in seg) {
                    y1 = seg.y1;
                }
                if ('y2' in seg) {
                    y2 = seg.y2;
                }
                if ('x' in seg) {
                    x = seg.x;
                }
                if ('y' in seg) {
                    y = seg.y;
                }
                switch (char) {
                    case 'M':
                        ctx.moveTo(x, y);
                        seg.x = x;
                        seg.y = y;
                        break;
                    case 'L':
                        ctx.lineTo(x, y);
                        seg.x = x;
                        seg.y = y;
                        break;
                    case 'C':
                        ctx.bezierCurveTo(x1, y1, x2, y2, x, y);
                        seg.x = x;
                        seg.y = y;
                        seg.x1 = x1;
                        seg.y1 = y1;
                        seg.x2 = x2;
                        seg.y2 = y2;
                        break;
                    case 'Q':
                        ctx.quadraticCurveTo(x1, y1, x, y);
                        seg.x = x;
                        seg.y = y;
                        seg.x1 = x1;
                        seg.y1 = y1;
                        break;
                    case 'A':
                        // eslint-disable-next-line
                        let curr = { x: x0, y: y0 };
                        let rx;
                        let ry;
                        if (options.arc && options.arc.length > 0) {
                            if (seg.r1 === 0) {
                                rx = options.arc[parseInt(arcCount.toString(), 10)].r1;
                            }
                            else {
                                rx = seg.r1;
                            }
                            if (seg.r2 === 0) {
                                ry = options.arc[parseInt(arcCount.toString(), 10)].r2;
                            }
                            else {
                                ry = seg.r2;
                            }
                            arcCount++;
                        }
                        else {
                            rx = seg.r1;
                            ry = seg.r2;
                        }
                        const xAxisRotation = seg.angle * (Math.PI / 180.0);
                        const largeArc = seg.largeArc;
                        const sweep = seg.sweep;
                        const cp = { x: x, y };
                        const currp = {
                            x: Math.cos(xAxisRotation) * (curr.x - cp.x) / 2.0 + Math.sin(xAxisRotation) * (curr.y - cp.y) / 2.0,
                            y: -Math.sin(xAxisRotation) * (curr.x - cp.x) / 2.0 + Math.cos(xAxisRotation) * (curr.y - cp.y) / 2.0
                        };
                        const l = Math.pow(currp.x, 2) / Math.pow(rx, 2) + Math.pow(currp.y, 2) / Math.pow(ry, 2);
                        if (l > 1) {
                            rx *= Math.sqrt(l);
                            ry *= Math.sqrt(l);
                        }
                        const k = (Math.pow(ry, 2) * Math.pow(currp.x, 2));
                        let s = (largeArc === sweep ? -1 : 1) * Math.sqrt(((Math.pow(rx, 2) * Math.pow(ry, 2)) - (Math.pow(rx, 2) * Math.pow(currp.y, 2)) - k) /
                            (Math.pow(rx, 2) * Math.pow(currp.y, 2) + Math.pow(ry, 2) * Math.pow(currp.x, 2)));
                        if (isNaN(s)) {
                            s = 0;
                        }
                        const cpp = { x: s * rx * currp.y / ry, y: s * -ry * currp.x / rx };
                        const centp = {
                            x: (curr.x + cp.x) / 2.0 + Math.cos(xAxisRotation) * cpp.x - Math.sin(xAxisRotation) * cpp.y,
                            y: (curr.y + cp.y) / 2.0 + Math.sin(xAxisRotation) * cpp.x + Math.cos(xAxisRotation) * cpp.y
                        };
                        const a1 = this.a([1, 0], [(currp.x - cpp.x) / rx, (currp.y - cpp.y) / ry]);
                        const u = [(currp.x - cpp.x) / rx, (currp.y - cpp.y) / ry];
                        const v = [(-currp.x - cpp.x) / rx, (-currp.y - cpp.y) / ry];
                        let ad = this.a(u, v);
                        if (this.r(u, v) <= -1) {
                            ad = Math.PI;
                        }
                        if (this.r(u, v) >= 1) {
                            ad = 0;
                        }
                        const dir = !sweep ? -1.0 : 1.0;
                        const ah = a1 + dir * (ad / 2.0);
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const halfWay = {
                            x: centp.x + rx * Math.cos(ah),
                            y: centp.y + ry * Math.sin(ah)
                        };
                        seg.centp = centp;
                        seg.xAxisRotation = xAxisRotation;
                        seg.rx = rx;
                        seg.ry = ry;
                        seg.a1 = a1;
                        seg.ad = ad;
                        seg.sweep = sweep;
                        if (ctx != null) {
                            const ra = rx > ry ? rx : ry;
                            const sx = rx > ry ? 1 : rx / ry;
                            const sy = rx > ry ? ry / rx : 1;
                            ctx.save();
                            ctx.translate(centp.x, centp.y);
                            ctx.rotate(xAxisRotation);
                            ctx.scale(sx, sy);
                            ctx.arc(0, 0, ra, a1, a1 + ad, !sweep);
                            ctx.scale(1 / sx, 1 / sy);
                            ctx.rotate(-xAxisRotation);
                            ctx.translate(-centp.x, -centp.y);
                            ctx.restore();
                        }
                        break;
                    case 'Z':
                    case 'z':
                        ctx.closePath();
                        x = x0;
                        y = y0;
                        break;
                }
                x0 = x;
                y0 = y;
            }
        }
    }
    /**
     * Draw the text element for the diagram\
     *
     *  @returns {void}  Draw the text element for the diagram .\
     *
     *  @param { SVGElement} canvas - Provide the SVG element .
     *  @param {TextAttributes} options - Provide the text element attributes .
     *  @param {SVGSVGElement} parentSvg - Provide the parent SVG element .
     *  @param {Object} ariaLabel - Provide the label properties .
     *  @param {string} diagramId - Provide the diagram id .
     *  @param {number} scaleValue - Provide the scale value .
     *  @param {any} renderer - Provide the renderer value .
     *  @param {element} element - Provide the text element value.
     *  @private
     */
    drawText(canvas, options, parentSvg, ariaLabel, diagramId, scaleValue, renderer, element) {
        if (options.content && options.visible === true) {
            const parentNode = renderer.groupElement;
            const ctx = CanvasRenderer.getContext(canvas);
            ctx.save();
            this.setStyle(canvas, options);
            if (scaleValue) {
                options.fontSize *= scaleValue;
            }
            const pivotX = options.x + options.width * options.pivotX;
            const pivotY = options.y + options.height * options.pivotY;
            // 919944: Text Flip and Rotation Not Applied in Exported Image
            this.applyFlipAndRotate(ctx, options, canvas, pivotX, pivotY, renderer, element);
            this.setFontStyle(canvas, options);
            let i = 0;
            let childNodes = [];
            childNodes = options.childNodes;
            const wrapBounds = options.wrapBounds;
            ctx.fillStyle = options.color;
            if (wrapBounds) {
                const position = this.labelAlign(options, wrapBounds, childNodes);
                for (i = 0; i < childNodes.length; i++) {
                    const child = childNodes[parseInt(i.toString(), 10)];
                    child.x = setChildPosition(child, childNodes, i, options);
                    const offsetX = position.x + (scaleValue ? child.x * scaleValue : child.x) - wrapBounds.x;
                    const offsetY = position.y + (scaleValue ? child.dy * scaleValue : child.dy) * i + ((options.fontSize) * 0.8);
                    if (wrapBounds.width > options.width && options.textOverflow !== 'Wrap' && options.textWrapping === 'NoWrap') {
                        child.text = overFlow(child.text, options);
                    }
                    if ((options.textOverflow === 'Clip' || options.textOverflow === 'Ellipsis') && options.textWrapping === 'Wrap') {
                        if (offsetY < parentNode.actualSize.height + parentNode.bounds.y) {
                            if (options.textOverflow === 'Ellipsis' && childNodes[i + 1]) {
                                const temp = childNodes[i + 1];
                                const y = position.y + temp.dy * (i + 1) + ((options.fontSize) * 0.8);
                                if (y > parentNode.actualSize.height + parentNode.bounds.y) {
                                    child.text = child.text.slice(0, child.text.length - 3);
                                    child.text = child.text.concat('...');
                                }
                            }
                            ctx.fillText(child.text, offsetX, offsetY);
                        }
                    }
                    else {
                        ctx.fillText(child.text, offsetX, offsetY);
                    }
                    if (options.textDecoration === 'Underline'
                        || options.textDecoration === 'Overline'
                        || options.textDecoration === 'LineThrough') {
                        const startPointX = offsetX;
                        let startPointY;
                        const textlength = ctx.measureText(child.text).width;
                        const endPointX = offsetX + textlength;
                        let endPointY;
                        switch (options.textDecoration) {
                            case 'Underline':
                                startPointY = offsetY + 2;
                                endPointY = offsetY + 2;
                                break;
                            case 'Overline':
                                startPointY = (position.y + child.dy * i);
                                endPointY = (position.y + child.dy * i);
                                break;
                            case 'LineThrough':
                                startPointY = ((offsetY + position.y + child.dy * i) / 2) + 2;
                                endPointY = ((offsetY + position.y + child.dy * i) / 2) + 2;
                        }
                        ctx.beginPath();
                        ctx.moveTo(startPointX, startPointY);
                        ctx.lineTo(endPointX, endPointY);
                        ctx.strokeStyle = options.color;
                        ctx.lineWidth = options.fontSize * .08;
                        ctx.globalAlpha = options.opacity;
                        ctx.stroke();
                    }
                }
            }
            ctx.restore();
        }
    }
    // 919944: Flip position and rotate angle calculation for elements
    applyFlipAndRotate(ctx, options, canvas, pivotX, pivotY, renderer, element) {
        if (options.flip !== FlipDirection.None && renderer && element && !(element.elementActions & ElementAction.ElementIsPort)) {
            const parent = renderer.groupElement;
            const textWrapper = element;
            let transform;
            if ((element instanceof TextElement && element.position)) {
                transform = renderer.renderFlipTextElement(parent, canvas, textWrapper, options.flip, options.flipMode, true);
            }
            else if (element instanceof PathElement || element instanceof ImageElement || (element instanceof TextElement
                && !element.position) || element instanceof DiagramElement) {
                transform = renderer.renderFlipElement(parent, canvas, options.flip, true);
            }
            transform = this.setElementTransform(element, renderer) || transform;
            //To set the translate and scale for the diagram elements while print and export.
            if (transform && transform.transform) {
                // Parse and apply the transform directly
                const transformRegex = /(translate|scale|rotate)\(([^)]+)\)/g;
                let match;
                // eslint-disable-next-line no-cond-assign
                while ((match = transformRegex.exec(transform.transform)) !== null) {
                    const type = match[1]; // translate, scale, rotate
                    const values = match[2].split(',').map((v) => parseFloat(v.trim()));
                    switch (type) {
                        case 'translate': {
                            const tx = values[0] || 0;
                            const ty = values[1] || 0;
                            ctx.translate(tx, ty);
                            break;
                        }
                        case 'scale': {
                            const sx = values[0] || 1;
                            const sy = values[1] || sx; // Use uniform scaling if sy is not specified
                            ctx.scale(sx, sy);
                            break;
                        }
                    }
                }
            }
        }
        this.rotateContext(canvas, options.angle, pivotX, pivotY);
    }
    setElementTransform(element, renderer) {
        const eventSuffixes = [
            '_cancel_1_event', '_cancel_0_event', '_cancel_2_trigger',
            '_success_0_event', '_success_1_event', '_success_2_trigger',
            '_failure_0_event', '_failure_1_event', '_failure_2_trigger'
        ];
        // Check if the id ends with the transaction sub type ids.
        for (const suffix of eventSuffixes) {
            if (element.id.endsWith(suffix)) {
                const bounds = renderer.transactionBounds;
                let posX = 0;
                let posY = 0;
                let scaleX = 1;
                let scaleY = 1;
                let offsetX = 0;
                let offsetY = 0;
                if (element.flip === 1 || element.flip === 3) {
                    posX = bounds.center.x;
                    scaleX = -1;
                    offsetX = -posX;
                }
                if (element.flip === 2 || element.flip === 3) {
                    posY = bounds.center.y;
                    scaleY = -1;
                    offsetY = -posY;
                }
                return {
                    transform: 'translate(' + posX + ',' + posY + ') scale(' + scaleX + ','
                        + scaleY + ') translate(' + offsetX + ',' + offsetY + ')'
                };
            }
        }
        return null;
    }
    loadImage(ctx, obj, canvas, pivotX, pivotY) {
        // 919944: Image Node Flip and Rotation Not Applied in Exported Image
        const image = new Image();
        image.src = obj.source;
        this.image(ctx, image, obj.x, obj.y, obj.width, obj.height, obj);
    }
    /**
     * Draw the image element for the diagram\
     *
     *  @returns {void} Draw the image element for the diagram .
     *  @param { SVGElement | HTMLCanvasElement} canvas - Provide the SVG element .
     *  @param {ImageAttributes} obj - Provide the image attributes .
     *  @param {SVGSVGElement} parentSvg - Provide the parent SVG element .
     *  @param {boolean} fromPalette - Provide the pointer event value .
     *  @param {any} renderer - provide renderer value
     *  @param {ImageElement} element - provide image element
     *  @private
     */
    drawImage(canvas, obj, parentSvg, fromPalette, renderer, element) {
        if (obj.visible) {
            const ctx = CanvasRenderer.getContext(canvas);
            ctx.save();
            const pivotX = obj.x + obj.width * obj.pivotX;
            const pivotY = obj.y + obj.height * obj.pivotY;
            const imageObj = new Image();
            imageObj.src = obj.source;
            // 919867: Opacity for the image node is not applied to the exported image
            this.setStyle(canvas, obj);
            const id = ctx.canvas.id.split('_');
            // eslint-disable-next-line
            const value = id[id.length - 1] === ('diagram' || 'diagramLayer') ? true : false;
            // eslint-disable-next-line
            /**
             *  Since Clipping portion for node with slice option is not calculated properly
             * if (obj.sourceX !== undefined && obj.sourceY !== undefined && obj.sourceWidth !== undefined
             *  && obj.sourceHeight !== undefined) {
             *  ctx.drawImage(imageObj, obj.sourceX, obj.sourceY, obj.sourceWidth, obj.sourceHeight, obj.x, obj.y, obj.width, obj.height);
             *  } else {
             *             ctx.drawImage(imageObj, obj.x, obj.y, obj.width, obj.height);
             * }
             */
            // 919944: Image Node Flip and Rotation Not Applied in Exported Image
            this.applyFlipAndRotate(ctx, obj, canvas, pivotX, pivotY, renderer, element);
            if (!fromPalette) {
                this.loadImage(ctx, obj, canvas, pivotX, pivotY);
            }
            else {
                imageObj.onload = () => {
                    this.loadImage(ctx, obj, canvas, pivotX, pivotY);
                };
            }
            ctx.restore();
        }
    }
    image(ctx, image, x, y, width, height, alignOptions) {
        ctx.beginPath();
        const srcWidth = image.width;
        const srcHeight = image.height;
        const destinationW = width;
        const destinationH = height;
        let resultWidth = 0;
        let resultHeight = 0;
        if (alignOptions && alignOptions.alignment !== 'None') {
            const xalign = alignOptions.alignment.toLowerCase().substr(1, 3);
            const yalign = alignOptions.alignment.toLowerCase().substr(5, 3);
            if (alignOptions.scale === 'Slice') {
                // eslint-disable-next-line
                const a = () => {
                    resultWidth = destinationW;
                    resultHeight = srcHeight * destinationW / srcWidth;
                };
                // eslint-disable-next-line
                const b = () => {
                    resultWidth = srcWidth * destinationH / srcHeight;
                    resultHeight = destinationH;
                };
                if (destinationW > destinationH) {
                    a();
                    if (destinationH > resultHeight) {
                        b();
                    }
                }
                else if (destinationW === destinationH) {
                    if (srcWidth > srcHeight) {
                        b();
                    }
                    else {
                        a();
                    }
                }
                else {
                    b();
                    if (destinationW > resultWidth) {
                        a();
                    }
                }
                const x1 = this.getSliceOffset(xalign, resultWidth, destinationW, srcWidth);
                const y1 = this.getSliceOffset(yalign, resultHeight, destinationH, srcHeight);
                const sWidth = srcWidth - x1;
                const sHeight = srcHeight - y1;
                const dWidth = resultWidth - (x1 * (resultWidth / srcWidth));
                const dHeight = resultHeight - (y1 * (resultHeight / srcHeight));
                const canvas1 = createHtmlElement('canvas', { 'width': width.toString(), 'height': height.toString() });
                const ctx1 = canvas1.getContext('2d');
                ctx1.drawImage(image, x1, y1, sWidth, sHeight, 0, 0, dWidth, dHeight);
                ctx.drawImage(canvas1, x, y, width, height);
            }
            else if (alignOptions.scale === 'Meet') {
                const srcRatio = (srcHeight / srcWidth);
                const destRatio = (destinationH / destinationW);
                resultWidth = destRatio > srcRatio ? destinationW : destinationH / srcRatio;
                resultHeight = destRatio > srcRatio ? destinationW * srcRatio : destinationH;
                x += this.getMeetOffset(xalign, resultWidth, destinationW);
                y += this.getMeetOffset(yalign, resultHeight, destinationH);
                ctx.drawImage(image, 0, 0, srcWidth, srcHeight, x, y, resultWidth, resultHeight);
            }
            else {
                ctx.drawImage(image, x, y, width, height);
            }
        }
        else {
            ctx.drawImage(image, x, y, width, height);
        }
        ctx.closePath();
    }
    getSliceOffset(arg, res, dest, src) {
        switch (arg) {
            case 'min': return 0;
            case 'mid': return (res - dest) / 2 * src / res;
            case 'max': return (res - dest) * src / res;
            default: return 0;
        }
    }
    getMeetOffset(arg, res, dest) {
        const max = Math.max(res, dest);
        const min = Math.min(res, dest);
        switch (arg) {
            case 'min': return 0;
            case 'mid': return (max - min) / 2;
            case 'max': return max - min;
            default: return 0;
        }
    }
    //end region
    // vector magnitude
    m(v) { return Math.sqrt(Math.pow(v[0], 2) + Math.pow(v[1], 2)); }
    // ratio between two vectors
    r(u, v) { return (u[0] * v[0] + u[1] * v[1]) / (this.m(u) * this.m(v)); }
    // angle between two vectors
    a(u, v) { return (u[0] * v[1] < u[1] * v[0] ? -1 : 1) * Math.acos(this.r(u, v)); }
    // text utility
    /**
     * Draw the SVG label.\
     *
     * @returns {PointModel} Draw the SVG label .
     *  @param {TextAttributes} text - Provide the canvas element .
     *  @param {Object} wrapBounds - Provide the canvas element .
     *  @param {SubTextElement []} childNodes - Provide the canvas element .
     * @private
     */
    labelAlign(text, wrapBounds, childNodes) {
        const bounds = new Size(wrapBounds.width, childNodes.length * (text.fontSize * 1.2));
        const position = { x: 0, y: 0 };
        const labelX = text.x;
        const labelY = text.y;
        const offsetx = text.width * 0.5;
        const offsety = text.height * 0.5;
        let pointx = offsetx;
        const pointy = offsety;
        if (text.textAlign === 'left') {
            pointx = 0;
        }
        else if (text.textAlign === 'center') {
            if (wrapBounds.width > text.width && (text.textOverflow === 'Ellipsis' || text.textOverflow === 'Clip')) {
                if (text.textWrapping === 'NoWrap') {
                    pointx = 0;
                }
                else {
                    pointx = text.width * 0.5;
                }
            }
            else {
                pointx = text.width * 0.5;
            }
        }
        else if (text.textAlign === 'right') {
            pointx = (text.width * 1);
        }
        position.x = labelX + pointx + (wrapBounds ? wrapBounds.x : 0);
        position.y = labelY + pointy - bounds.height / 2;
        return position;
    }
}
